import { useEffect, useState } from 'react';
import type { Suggestion } from '../../shared/types';
import { ApiError, clientErrorMessage } from '../lib/api';
import Eyebrow from './Eyebrow';
import Button from './Button';
import MobileSheet from './MobileSheet';
import { useMediaQuery } from '../lib/useMediaQuery';

const TIP_DISMISS_KEY = 'hsp_compare_tip_dismissed';

interface Props {
  suggestion: Suggestion;
  onAccept: () => Promise<void>;
  onMarkReviewed: () => Promise<void>;
  onReject: (note: string) => Promise<void>;
}

export default function CompareReviewBar({
  suggestion,
  onAccept,
  onMarkReviewed,
  onReject,
}: Props) {
  const desktop = useMediaQuery('(min-width: 768px)');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<'accept' | 'reject' | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [showTip, setShowTip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicted, setConflicted] = useState(false);
  const [retry, setRetry] = useState<(() => Promise<void>) | null>(null);

  // Compare rebases the client changes onto the current working copy.
  useEffect(() => {
    try {
      if (!localStorage.getItem(TIP_DISMISS_KEY)) setShowTip(true);
    } catch {
      // Safari private mode — just show the tip; harmless.
      setShowTip(true);
    }
  }, []);

  function dismissTip() {
    setShowTip(false);
    try {
      localStorage.setItem(TIP_DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  }

  const submittedAgo = suggestion.submitted_at ? timeAgo(suggestion.submitted_at) : 'recently';
  const author = suggestion.author_name || 'Client';

  // Saves in flight can be retried; same-row merge conflicts need a per-change choice.
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setConflicted(false);
    setRetry(null);
    try {
      await action();
      setConfirming(null);
    } catch (err) {
      const body = err instanceof ApiError ? err.body as { conflict?: boolean; stale?: boolean } | undefined : undefined;
      const stale = err instanceof ApiError && err.status === 409 && body?.stale === true;
      const isConflict = !stale && body?.conflict === true;
      setError(stale ? 'Your last edit was still saving. Try again in a moment.' : clientErrorMessage(err, 'That didn’t go through. Please try again.'));
      if (stale) setRetry(() => action);
      setConflicted(isConflict);
      // A conflict can't be retried, so drop the confirm panel and let the
      // guidance below stand. Anything else might work on a second press —
      // keep the panel (and any typed rejection note) as it was.
      if (isConflict) setConfirming(null);
    } finally {
      setBusy(false);
    }
  }

  const controls = (
    <div className="mobile-compare-bar bg-card border-b border-accent/50 sticky top-[85px] md:top-[53px] z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow accent>// Comparing suggestion</Eyebrow>
          <p className="text-fg text-[13px] mt-1">
            <span className="font-medium">{author}</span>
            <span className="text-faint"> · submitted {submittedAgo}</span>
            {suggestion.note && (
              <span className="text-muted italic"> · "{truncate(suggestion.note, 80)}"</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirming('reject')}
            disabled={busy}
          >
            Reject
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => run(onMarkReviewed)}
            disabled={busy}
            title="Close the suggestion after applying the changes you want in Compare."
          >
            Mark reviewed
          </Button>
          <Button
            variant="primary"
            size="sm"
            glow
            onClick={() => setConfirming('accept')}
            disabled={busy}
          >
            Accept changes
          </Button>
        </div>
      </div>

      {error && (
        <div className="border-t border-error/40 bg-error/5 px-6 md:px-8 py-3">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-error mb-1">
                // Couldn’t accept
              </div>
              <p className="text-fg text-[13px] leading-[1.55]">{error}</p>
              {retry && <button type="button" onClick={() => void run(retry)} disabled={busy} className="text-accent text-[13px] mt-2">Retry</button>}
              {conflicted && (
                <p className="text-muted text-[13px] leading-[1.55] mt-1.5">
                  You and the client changed the same rows. Apply the parts you want with the ✓
                  buttons in the diff, then <span className="text-fg">Mark reviewed</span> to close
                  it out, or <span className="text-fg">Reject</span> and ask for a fresh pass.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setConflicted(false);
              }}
              className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint hover:text-fg transition-colors shrink-0"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {showTip && (
        <div className="border-t border-line bg-elevated/60 px-6 md:px-8 py-2.5">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-4">
            <p className="text-muted text-[12px] leading-[1.55]">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint mr-2">
                // Tip
              </span>
              Your edits are kept as you compare. The diff shows the client's changes rebased onto
              your working copy. Use ✓ to apply a change or × to leave it out.
            </p>
            <button
              type="button"
              onClick={dismissTip}
              className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint hover:text-fg transition-colors shrink-0"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {confirming === 'accept' && (
        <div className="border-t border-accent/30 bg-accent/8 px-6 md:px-8 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <p className="text-fg text-[13px]">
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-accent mr-2">
                // Confirm
              </span>
              This merges the client's changes into your working copy. Your edits to other rows are kept.
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => run(onAccept)}
                disabled={busy}
              >
                {busy ? 'Applying…' : 'Yes, apply changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirming === 'reject' && (
        <div className="border-t border-accent/30 bg-accent/5 px-6 md:px-8 py-3">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            <label className="flex-1 min-w-0">
              <span className="block font-mono text-[10px] uppercase tracking-[0.12em] text-muted mb-1">
                Reason for rejecting (optional, sent back to client)
              </span>
              <input
                type="text"
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                className="
                  w-full px-3 py-2 bg-elevated border border-line rounded-sm
                  text-fg text-[13px]
                  focus:border-accent focus:shadow-focus focus:outline-none
                  transition-all duration-150
                "
              />
            </label>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => run(() => onReject(rejectionNote))}
                disabled={busy}
              >
                {busy ? 'Rejecting…' : 'Confirm reject'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  if (desktop) return controls;
  return <div className="mobile-compare-actions" data-print-hide>
    <p>{author} · submitted {submittedAgo}</p>
    {showTip && <div className="mobile-compare-tip" role="note">Your edits are kept. The diff shows the client's changes rebased onto your working copy. <button onClick={dismissTip}>Got it</button></div>}
    <button onClick={() => setActionsOpen(true)}>Whole-suggestion actions</button>
    <MobileSheet open={actionsOpen} title="Whole-suggestion actions" onClose={() => setActionsOpen(false)}>{controls}</MobileSheet>
  </div>;

}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;

}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
